import React, { useState, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Tooltip } from 'antd';
import { es } from 'date-fns/locale';
import { IGroupedShareCompareHotel, IQuote } from '@share/common-types';
import { RefundableEnum } from '@common-types';
import { IMarginatorState, ILoginState } from '@share/store/slices';
import { formatDateCheckInOut, renderNumber, isSpanish, } from '@share/utils';
import { getAmenityImage } from '@utils';
import { Currency, HotelStars } from '@components';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import NoImage from '@assets/images/amenities/no-picture.png';
import { getModalCompareMealPlanBadgeKind, MODAL_COMPARE_MEAL_PLAN_BADGE_COLORS } from '../../../result/result-wrapper/modal-compare/meal-plan-badge-theme';
import { EnvironmentOutlined, CalendarOutlined } from "@ant-design/icons";
import { ExpiredQuoteNotice } from '../expired-notice';
import { buildSearchAgainUrl } from '../search-again-url';

interface IHotelTabProps {
  hotels: Record<string, IGroupedShareCompareHotel[]>;
  marginatorStore: IMarginatorState;
  loginStore: ILoginState;
  onCancel?: () => void;
  isHistorical?: boolean;
}

const percents = 100;

// A hotel quote's room/guest "distribution" arrives either as a human-readable string
// (e.g. package hotels: "1 Room, 2 Adults") or as the raw rooms JSON for standalone hotel
// quotes (e.g. [{"adultsCount":2,"kids":[]}]). Returns the parsed rooms array for the JSON
// form (so we can render "N Rooms - X Adults - Y Children"), or null for a plain string
// that should be shown as-is.
const parseRoomsDistribution = (value?: string): any[] | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed[0] !== '[' && trimmed[0] !== '{') return null;
  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return null;
  }
};

export const HotelTab: React.FC<IHotelTabProps> = ({
  hotels,
  marginatorStore,
  loginStore,
  isHistorical,
}) => {
  const locations = Object.keys(hotels);
  const [selectedLocation, setSelectedLocation] = useState(locations[0] || '');
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);

  const currentGroups = hotels[selectedLocation] || [];

  // "Search again" link for the expired notice: derived from the first property of the
  // currently selected city, so switching city tabs retargets the search.
  const searchAgainUrl = useMemo(
    () => buildSearchAgainUrl((hotels[selectedLocation]?.[0]?.items?.[0] as any)?.link, 'hotel'),
    [hotels, selectedLocation]
  );

  useEffect(() => {
    if (!selectedLocation && locations.length > 0) {
      setSelectedLocation(locations[0]);
    } else if (selectedLocation && !hotels[selectedLocation]) {
      setSelectedLocation(locations[0] || '');
    }
  }, [hotels]);

  useEffect(() => {
    if (activeGroupIndex >= currentGroups.length && currentGroups.length > 0) {
      setActiveGroupIndex(currentGroups.length - 1);
    }
  }, [currentGroups.length]);

  const { account } = loginStore;
  const isB2C = account?.isB2C;
  const compareRowHasMealPlan = currentGroups[activeGroupIndex]?.items.some((h) => !isEmpty(h.mealPLan?.trim()));

  const handleView = (hotel:  IQuote) => {
    const link = hotel.link.startsWith('http') ? hotel.link : `${window.location.origin}${hotel.link}`;
    const [basePart, queryPart] = link.split('?');
    const queryWithoutQuote = (queryPart || '')
      .replace(/&?quote=[^&]*/g, '')
      .replace(/^&/, '');
    const linkWithoutQuote = queryWithoutQuote ? `${basePart}?${queryWithoutQuote}` : basePart;
    const sep = linkWithoutQuote.includes('?') ? '&' : '?';
    window.open(`${linkWithoutQuote}${sep}quote=${hotel.userWhoShared}`, '_blank');
  };

  const hotel = currentGroups[activeGroupIndex]?.items[0];
  const locationName = hotel?.locationObj?.name || selectedLocation;
  const activeGroup: any = currentGroups[activeGroupIndex] || {};
  const rawRoomDetail = (activeGroup?.roomsDetail || hotel?.roomsDetail || hotel?.occupancy || '').toString().trim();
  const parsedRooms = parseRoomsDistribution(rawRoomDetail);
  // Plain string -> show it directly; JSON rooms -> derive the counts below and hide the raw text.
  const roomDetailText = parsedRooms ? '' : rawRoomDetail;
  const roomsForCount: any[] = parsedRooms ?? (hotel?.rooms as any[] | undefined) ?? [];
  const roomsCountMeta = roomsForCount.length;
  const adultsCountMeta = roomsForCount.reduce((acc: number, room: any) => acc + (room?.adultsCount ?? room?.adults ?? 0), 0);
  const kidsCountMeta = roomsForCount.reduce((acc: number, room: any) => acc + ((room?.kids ?? room?.kidsAges ?? room?.children ?? []).length || 0), 0);

  return (
    <div>
      {(locations.length > 1 || currentGroups.length > 1) && (
        <div className="hotels-wrapper">
          {/* LOCATION TABS */}
          {locations.length > 1 && (
            <div className="hotels-row">
              <div className="icon-box">
                <EnvironmentOutlined />
              </div>

              <div className="tabs-container locations-tabs">
                {locations.map((locCode) => (
                  <button
                    key={locCode}
                    className={`tab-btn ${selectedLocation === locCode ? "active" : ""}`}
                    onClick={() => {
                      setSelectedLocation(locCode);
                      setActiveGroupIndex(0);
                    }}
                  >
                    {hotels[locCode][0]?.items[0]?.locationObj?.name || locCode}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* DATE + GUEST TABS */}
          {currentGroups.length > 1 && (
            <div className="hotels-row">
              <div className="icon-box">
                <CalendarOutlined />
              </div>

              <div className="tabs-container">
                {currentGroups.map((group, index) => {
                  return (
                    <button
                      key={index}
                      className={`tab-btn ${activeGroupIndex === index ? "active" : ""}`}
                      onClick={() => setActiveGroupIndex(index)}
                    >
                      {formatDateCheckInOut(group.startDate, DATE_FORMAT_CHECK_IN_OUT)} - {formatDateCheckInOut(group.endDate, DATE_FORMAT_CHECK_IN_OUT)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className='modal-compare__hotels-location'>
        {locationName ? <div>{locationName}</div> : null}
      </div>

      <div className="modal-compare__hotels modal-compare__hotels--meta" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className='modal-compare__hotels_link'>
          {hotel && (
            <>
              <div className="hotels-dates">
                {formatDateCheckInOut(hotel.startDate, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)} -{' '}
                {formatDateCheckInOut(hotel.endDate, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)}
              </div>
              <div className='modal-compare__hotels_link_separator'>-</div>
              <div className="hotels-dates">
                {roomDetailText ? (
                  roomDetailText
                ) : (
                  <>
                    {roomsCountMeta} <FormattedMessage id="room" values={{ count: roomsCountMeta }} /> - {adultsCountMeta}{' '}
                    <FormattedMessage id="adult" values={{ count: adultsCountMeta }} /> - {kidsCountMeta}{' '}
                    <FormattedMessage id="child" values={{ count: kidsCountMeta }} />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {isHistorical ? <ExpiredQuoteNotice searchAgainUrl={searchAgainUrl} /> : null}

      <div className="modal-compare__hotels">
        {currentGroups[activeGroupIndex]?.items.map((hotelItem, index) => {
          const { price } = hotelItem;

          const value = marginatorStore.value || 0;
          const marginatorPrice = price / (1 - value / 100);

          const publicPrice =
            isB2C || !hotelItem.publicPrices?.length ? null : Math.min(...hotelItem.publicPrices.map((p) => p.price));

          const msave = publicPrice ? (marginatorPrice * percents) / publicPrice : 100;
          const savings = Math.floor(percents - msave);

          return (
            <div key={index} className="modal-compare__hotel">
              <div className="modal-compare__media">
                <div className="hotel-images-container">
                  <div className="hotel-image" style={{ backgroundImage: `url(${get(hotelItem, 'images[0].url')})` }} />
                  {savings > 0 ? (
                    <div className="modal-compare__savings-badge">
                      {savings.toFixed(0)} % <FormattedMessage id="results.hotel.card.compare.modal.off" />
                    </div>
                  ) : null}
                </div>
              </div>

              <div
                className={`hotel-text-container hotel-text-container--title${compareRowHasMealPlan ? ' hotel-text-container--title-with-mealplan' : ''
                  }`}
              >
                {compareRowHasMealPlan ? (
                  <div className="modal-compare__title-mealplan-row">
                    <div className="modal-compare__title-col">
                      <Tooltip title={hotelItem.displayName} placement="top">
                        <p>{hotelItem.displayName}</p>
                      </Tooltip>
                    </div>
                    <div className="modal-compare__meal-plan-col">
                      {!isEmpty(hotelItem.mealPLan?.trim()) ? (
                        <span
                          className="modal-compare__meal-plan-badge"
                          style={
                            MODAL_COMPARE_MEAL_PLAN_BADGE_COLORS[
                            getModalCompareMealPlanBadgeKind(hotelItem.mealPLan)
                            ]
                          }
                        >
                          {hotelItem.mealPLan}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <Tooltip title={hotelItem.displayName} placement="top">
                    <p>{hotelItem.displayName}</p>
                  </Tooltip>
                )}
              </div>

              <div className="hotel-text-container hotel-text-container--address">
                <Tooltip
                  title={`${hotelItem.address}, ${hotelItem.city}, ${hotelItem.state}, ${hotelItem.countryCode}`}
                  placement="top"
                >
                  <p>
                    {hotelItem.address}, {hotelItem.city}, {hotelItem.state}, {hotelItem.countryCode}
                  </p>
                </Tooltip>
              </div>

              {hotelItem.refundability && hotelItem.refundability !== RefundableEnum.Unknown ? (
                <div className="hotel-text-container hotel-text-container--refundable">
                  <div className="hotel-card-wrapper__refundable">
                    {hotelItem.refundability === RefundableEnum.Refundable && (
                      <div className="hotel-card-wrapper__refundable-ref">
                        <FormattedMessage id="refundable" />
                      </div>
                    )}
                    {hotelItem.refundability === RefundableEnum.Nonrefundable && (
                      <div className="hotel-card-wrapper__refundable-nonref">
                        <FormattedMessage id="non.refundable" />
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {hotelItem.starRating >= 0 || hotelItem.rating?.value || hotelItem.rating?.reviewsCount ? (
                <div className="hotel-amenities-container">
                  {hotelItem.starRating >= 0 ? <HotelStars stars={hotelItem.starRating} /> : null}
                  <label>
                    {hotelItem.rating?.value ? (
                      <>
                        <strong>{hotelItem.rating?.value}</strong>/10
                      </>
                    ) : null}{' '}
                    {hotelItem.rating?.reviewsCount ? (
                      <>
                        ({hotelItem.rating?.reviewsCount} <FormattedMessage id="reviews" />)
                      </>
                    ) : null}
                  </label>
                </div>
              ) : null}

              <div className="hotel-text-container hotel-text-container--price">
                {publicPrice ? (
                  <p style={{ textDecoration: 'line-through' }}>
                    <Currency currency={hotelItem.currency} />
                    {renderNumber(publicPrice, 2)}
                  </p>
                ) : null}
                <p>
                  <strong>
                    <Currency currency={hotelItem.currency} />
                    {renderNumber(marginatorPrice, 2)}
                  </strong>
                </p>
              </div>

              <div
                className={`hotel-text-container${isHistorical ? ' hotel-text-container--flush-bottom' : ''}`}
                style={{ height: '87px' }}
              >
                {hotelItem?.amenities?.length ? (
                  <>
                    {hotelItem?.amenities?.filter((a: any) => !isEmpty(a))?.length > 0 ? (
                      hotelItem
                        ?.amenities.map((a: any) => ({ ...(getAmenityImage(a) as Record<string, unknown>), name: a.name }))
                        .filter((a: any) => !!a)
                        .slice(0, 2)
                        .map((a: any, i: number) => (
                          <div key={i} className="modal-compare__amenity-row">
                            <img src={(a as { image?: string }).image || NoImage} alt={a.name || 'no-picture'} width="33" />
                            <div className="modal-compare__amenity-label-wrap">
                              <Tooltip title={a.name || ''} placement="top">
                                <span className="modal-compare__amenity-name">{a.name}</span>
                              </Tooltip>
                            </div>
                          </div>
                        ))
                    ) : (
                      <FormattedMessage id="results.hotel.card.compare.modal.no_amenities" />
                    )}
                  </>
                ) : (
                  <FormattedMessage id="results.hotel.card.compare.modal.no_amenities" />
                )}
              </div>

              {!isHistorical ? (
                <div className="hotel-view-container">
                  <div
                    className="view-button"
                    style={(hotelItem as any).isPast ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    onClick={() => { if (!(hotelItem as any).isPast) handleView(hotelItem as any as IQuote); }}
                  >
                    <FormattedMessage id="results.hotel.card.compare.modal.view" />
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

